import React, { useEffect, useState, useRef, useMemo, FC } from 'react';
import { Box, Stack } from '@mui/material';
import felixLoading from '../../../AIStudio/SharedComponents/Graphics/felix-loading.json';
import { generateLoadingText } from '../config';
import Lottie from 'react-lottie';
import { classes } from '../../../AIStudio/SharedComponents/styles';
import { Info as InfoIcon } from '@mui/icons-material';

interface LoadingOverlayProps {
  isLoading: boolean;
  generationType?: string;
  customMessages?: string[];
  showSocketMessage?: boolean;
}

export const LoadingOverlay: FC<LoadingOverlayProps> = ({
  isLoading,
  generationType,
  customMessages,
  showSocketMessage = false
}) => {
  const [loadingTextIndex, setLoadingTextIndex] = useState(0);
  const changeTextIndex = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadingAnimation = {
    loop: true,
    autoplay: true,
    animationData: felixLoading,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice'
    }
  };

  const loadingText = useMemo(() => {
    if (generationType === 'custom' && customMessages) {
      return customMessages;
    }
    return generateLoadingText(generationType || '');
  }, [customMessages, generationType]);

  useEffect(() => {
    changeTextIndex.current = setTimeout(() => {
      if (loadingTextIndex < loadingText.length - 1) {
        setLoadingTextIndex((prev) => prev + 1);
      } else {
        setLoadingTextIndex(0);
      }
    }, 5000);
    return () => {
      if (changeTextIndex.current) {
        clearTimeout(changeTextIndex.current);
      }
    };
  }, [loadingText.length, loadingTextIndex]);

  if (!isLoading) return null;

  return (
    <Box sx={classes.loadingOverlay}>
      <Stack direction="column" mb={'200px'} spacing={2}>
        <Lottie options={loadingAnimation} height={450} width={220} />
        <Box sx={classes.loadingMessage}>
          {loadingText[loadingTextIndex as keyof typeof loadingText]}
        </Box>
        {showSocketMessage && (
          <Box sx={{ ...classes.socketsMessage, ...(showSocketMessage && { opacity: '1px' }) }}>
            <InfoIcon sx={classes.socketsInfoIcon} />
            <span>
              You can close this at anytime and Felix will let you know when your content has been
              generated.
            </span>
          </Box>
        )}
      </Stack>
    </Box>
  );
};
